/**
 * CÁ NHÂN — hồ sơ, lối vào các mục con, và đăng xuất.
 *
 * Bố cục theo bản thiết kế `fe/trang-ca-nhan.pdf`: thẻ hồ sơ trên cùng, rồi
 * các nhóm menu, rồi nút đăng xuất đỏ ở dưới.
 *
 * Phần chi tiết (thiết bị, khuôn mặt, quyền dữ liệu) chuyển sang màn con
 * `ThongTinCaNhan` — trước đây tất cả nằm chung một trang dài, phải cuộn qua
 * năm khối mới tới nút đăng xuất.
 *
 * Hàng đợi đồng bộ GIỮ LẠI ở đây, không chuyển đi: đó là thứ nhân viên cần
 * thấy ngay khi mở tab này ("bản ghi của tôi gửi được chưa?"), và nó phải nằm
 * trên cùng đường đi tới nút Đăng xuất để người ta không thoát khi còn bản
 * ghi chưa gửi.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import DoiMatKhau from './DoiMatKhau'
import { ngayGio } from '../lib/gio'
import { db, flush, pendingCount, type QueuedPunch } from '../lib/queue'
import { chuCaiDau } from '../lib/ten'
import { dangXuat, nhanVienHienTai } from '../services/auth.service'
import { Icon, useThongBao } from '../ui'

const VAI_TRO: Record<string, string> = {
  admin: 'Quản trị viên',
  manager: 'Quản lý',
  supervisor: 'Tổ trưởng',
  employee: 'Nhân viên',
}

export default function CaNhan() {
  const tb = useThongBao()
  const dieuHuong = useNavigate()

  const [choDongBo, setChoDongBo] = useState(0)
  const [thatBai, setThatBai] = useState<QueuedPunch[]>([])
  const [dangGui, setDangGui] = useState(false)
  const [doiMatKhau, setDoiMatKhau] = useState(false)
  const [dangThoat, setDangThoat] = useState(false)

  const nv = nhanVienHienTai()

  const nap = async () => {
    setChoDongBo(await pendingCount())
    setThatBai(await db.punches.where('state').equals('failed').toArray())
  }

  useEffect(() => { void nap() }, [])

  const guiLai = async () => {
    setDangGui(true)
    // Đưa các bản thất bại về hạn ngay, không chờ hết backoff
    await db.punches.where('state').equals('failed')
      .modify({ next_attempt_at: Date.now() })
    await flush()
    await nap()
    setDangGui(false)
  }

  // Đổi mật khẩu TỰ NGUYỆN — khác màn bắt buộc ở App.tsx đúng một chỗ: có nút
  // "Để sau". Lối vào này là thứ làm tuần đệm của quy trình phát hành chạy
  // được (docs/14 §14.6 bước 2): nhân viên tự đổi trước ngày bị ép.
  if (doiMatKhau) {
    return (
      <DoiMatKhau
        onXong={() => {
          setDoiMatKhau(false)
          tb.bao_xong('Đã đổi mật khẩu')
        }}
        onHuy={() => setDoiMatKhau(false)}
      />
    )
  }

  return (
    <div className="man-hinh" style={{ display: 'grid', gap: 'var(--k4)' }}>
      <header className="dau-trang"><h1>Cá nhân</h1></header>

      {nv && (
        <section className="the-ho-so">
          <div className="ho-so-dau">
            <span className="ho-so-anh" aria-hidden="true">
              {chuCaiDau(nv.full_name)}
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="ho-so-ten">{nv.full_name}</div>
              <span className="the-nhan tot">Đang làm việc</span>
            </div>
          </div>

          <div>
            <div className="hang-thong-tin">
              <span className="nhan">Mã nhân viên</span>
              <span className="gia-tri">{nv.code}</span>
            </div>
            <div className="hang-thong-tin">
              <span className="nhan">Vai trò</span>
              <span className="gia-tri">{VAI_TRO[nv.role] ?? nv.role}</span>
            </div>
            <div className="hang-thong-tin">
              <span className="nhan">Thiết bị đã đăng ký</span>
              <span className="gia-tri">
                {!nv.device_bound
                  ? <span style={{ color: 'var(--canh-bao)' }}>Chưa đăng ký</span>
                  : nv.device_matches
                    ? <span style={{ color: 'var(--chinh-chu)' }}>Đúng máy này</span>
                    : <span style={{ color: 'var(--loi)' }}>Máy khác</span>}
              </span>
            </div>
          </div>

          {/* Bản thiết kế có "Phòng ban" và "Chức vụ". `/auth/login` không trả
              hai trường đó, và bảng `employees` không có cột chức vụ — hiện
              một ô trống ở đây trông như dữ liệu bị mất, nên nói thẳng. */}
          <p className="ghi-chu-rieng-tu" style={{ textAlign: 'left', maxWidth: 'none' }}>
            Phòng ban và chức vụ do bộ phận nhân sự quản lý, chưa hiển thị trong
            ứng dụng này.
          </p>
        </section>
      )}

      {/* Hàng đợi: đặt TRƯỚC menu vì đây là thứ cần thấy ngay. */}
      <section className={choDongBo > 0 ? 'the-khoi' : 'the-khoi'}>
        <h2 style={{ fontSize: 'var(--cy-muc)', marginBottom: 'var(--k2)' }}>
          Hàng đợi đồng bộ
        </h2>

        {choDongBo === 0 ? (
          <p className="trang-thai-tot" style={{ margin: 0 }}>
            Đã đồng bộ hết. Không còn bản ghi nào chờ.
          </p>
        ) : (
          <p className="trang-thai-cho" style={{ margin: 0 }}>
            {choDongBo} bản ghi đang chờ gửi lên hệ thống.
          </p>
        )}

        {thatBai.length > 0 && (
          <>
            <ul className="danh-sach-that-bai">
              {thatBai.map((p) => (
                <li key={p.client_uuid}>
                  <span className="that-bai-gio">{ngayGio(p.queued_at)}</span>{' '}
                  <span className="that-bai-huong">
                    {p.direction === 'in' ? 'Vào ca' : 'Ra ca'}
                  </span>
                  <span className="that-bai-loi">{p.last_error}</span>
                </li>
              ))}
            </ul>
            <button type="button" className="nut-phu" disabled={dangGui}
                    onClick={() => void guiLai()}>
              {dangGui ? 'Đang gửi…' : 'Thử gửi lại ngay'}
            </button>
          </>
        )}

        <p className="ghi-chu">
          Bản ghi chấm công được lưu ngay trên máy trước khi gửi. Kể cả hết pin
          hay mất mạng nhiều ngày, dữ liệu vẫn còn và sẽ tự gửi khi có mạng.
        </p>
      </section>

      <nav className="nhom-menu" aria-label="Tài khoản">
        <p className="nhom-menu-tieu-de">Tài khoản</p>

        <button type="button" className="muc-ca-nhan"
                onClick={() => dieuHuong('/ca-nhan/thong-tin')}>
          <span className="muc-ca-nhan-icon"><Icon ten="nguoi-dung" size={20} /></span>
          <span>Thông tin cá nhân</span>
          <span />
          <Icon ten="mui-ten-phai" size={18} className="mui-ten" />
        </button>

        <button type="button" className="muc-ca-nhan"
                onClick={() => setDoiMatKhau(true)}>
          <span className="muc-ca-nhan-icon"><Icon ten="khoa" size={20} /></span>
          <span>Đổi mật khẩu</span>
          <span />
          <Icon ten="mui-ten-phai" size={18} className="mui-ten" />
        </button>
      </nav>

      {nv && ['supervisor', 'manager', 'admin'].includes(nv.role) && (
        <nav className="nhom-menu" aria-label="Tổ trưởng">
          <p className="nhom-menu-tieu-de">Tổ trưởng</p>
          <Link to="/ca-cong-trinh" className="muc-ca-nhan"
                style={{ textDecoration: 'none' }}>
            <span className="muc-ca-nhan-icon"><Icon ten="co" size={20} /></span>
            <span>
              Mở ca công trình
              <span className="gia-tri-phu" style={{ display: 'block' }}>
                Lấy mã 6 số cho thợ chấm công
              </span>
            </span>
            <span />
            <Icon ten="mui-ten-phai" size={18} className="mui-ten" />
          </Link>
        </nav>
      )}

      {/*
        Hai mục dưới đây có trong bản thiết kế nhưng KHÔNG có backend:
        không bảng cài đặt thông báo, không hệ thống đa ngôn ngữ (toàn bộ
        giao diện và thông điệp lỗi của server đều là tiếng Việt).

        Hiện chúng ở trạng thái vô hiệu hoá kèm lời giải thích, thay vì bấm
        vào rồi không có gì xảy ra — một nút không phản hồi làm người dùng
        tưởng ứng dụng treo.
      */}
      <nav className="nhom-menu" aria-label="Cài đặt">
        <p className="nhom-menu-tieu-de">Cài đặt</p>

        <button type="button" className="muc-ca-nhan" disabled
                style={{ opacity: 0.5, cursor: 'not-allowed' }}>
          <span className="muc-ca-nhan-icon"><Icon ten="chuong" size={20} /></span>
          <span>
            Cài đặt thông báo
            <span className="gia-tri-phu" style={{ display: 'block' }}>
              Hệ thống chưa gửi thông báo đẩy
            </span>
          </span>
          <span />
          <span />
        </button>

        <button type="button" className="muc-ca-nhan" disabled
                style={{ opacity: 0.5, cursor: 'not-allowed' }}>
          <span className="muc-ca-nhan-icon"><Icon ten="toan-cau" size={20} /></span>
          <span>Ngôn ngữ</span>
          <span className="gia-tri-phu">Tiếng Việt</span>
          <span />
        </button>
      </nav>

      <button
        type="button"
        className="nut-thoat"
        disabled={dangThoat}
        onClick={() => {
          setDangThoat(true)
          // Luôn về màn đăng nhập, kể cả khi gọi server hỏng: `dangXuat()` dọn
          // localStorage trong khối `finally`. Nạp lại trang để mọi trạng thái
          // trong bộ nhớ biến mất cùng lúc.
          void dangXuat().finally(() => location.reload())
        }}
      >
        {dangThoat ? 'Đang thoát…' : 'Đăng xuất'}
      </button>

      {/* Cảnh báo thật: đăng xuất KHÔNG xoá hàng đợi, nhưng người dùng cần biết */}
      {choDongBo > 0 && (
        <p className="canh-bao-dang-xuat" role="alert">
          Còn {choDongBo} bản ghi chưa gửi. Chúng <strong>không bị mất</strong>{' '}
          khi đăng xuất, nhưng chỉ gửi được sau khi bạn đăng nhập lại — nên chờ
          đồng bộ xong thì hơn.
        </p>
      )}
    </div>
  )
}
